import { useCallback, useEffect, useRef, useState } from "react";
import { guestOpacApi, type GuestOpacResourceSummary } from "@/lib/api/guest-opac";
import type { GuestCatalogType } from "@/hooks/useGuestOpacTenantSlug";

const PAGE_SIZE = 24;

interface UseGuestCatalogFeedOptions {
  tenantSlug?: string;
  searchQuery: string;
  type: GuestCatalogType;
  enabled: boolean;
}

export function useGuestCatalogFeed({
  tenantSlug,
  searchQuery,
  type,
  enabled,
}: UseGuestCatalogFeedOptions) {
  const [items, setItems] = useState<GuestOpacResourceSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pageRef = useRef(1);
  const requestIdRef = useRef(0);

  const fetchPage = useCallback(
    async (page: number, mode: "replace" | "append") => {
      if (!tenantSlug) return;
      const requestId = ++requestIdRef.current;
      if (mode === "replace") setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const res = searchQuery
          ? await guestOpacApi.search(
              { q: searchQuery, type, page, pageSize: PAGE_SIZE },
              tenantSlug
            )
          : await guestOpacApi.listCatalog({ type, page, pageSize: PAGE_SIZE }, tenantSlug);
        if (requestId !== requestIdRef.current) return;
        setTotal(res.total);
        pageRef.current = page;
        setItems((prev) => (mode === "append" ? [...prev, ...res.items] : res.items));
      } catch {
        if (requestId !== requestIdRef.current) return;
        setError("Unable to load the catalog. Please try again.");
        if (mode === "replace") {
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [tenantSlug, searchQuery, type]
  );

  useEffect(() => {
    if (!enabled || !tenantSlug) {
      setLoading(false);
      setItems([]);
      setTotal(0);
      return;
    }
    pageRef.current = 1;
    void fetchPage(1, "replace");
  }, [enabled, tenantSlug, searchQuery, type, fetchPage]);

  const loadMore = useCallback(() => {
    if (!tenantSlug || loading || loadingMore) return;
    if (items.length >= total || total === 0) return;
    void fetchPage(pageRef.current + 1, "append");
  }, [tenantSlug, loading, loadingMore, items.length, total, fetchPage]);

  const hasMore = items.length < total;

  return {
    items,
    total,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    retry: () => fetchPage(1, "replace"),
  };
}
